import { useState } from 'react';
import { DISPATCH_COLUMN } from '../../../../constants';

/**
 * useDispatchFilters - Hook for managing search and sort/filter state
 *
 * This hook handles:
 * - Search term state and search trigger flag
 * - Sort/filter dropdown values (dispatch status, state, bill status)
 * - Sorting array options for dropdowns (populated externally)
 * - Utility functions for building API payloads
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.initialState - Initial values from URL
 * @param {string} options.initialState.searchTerm - Initial search term
 * @param {string} options.initialState.dispatchStatusFilter - Initial dispatch status filter
 * @param {string} options.initialState.stateFilter - Initial state filter
 * @param {string} options.initialState.billStatusFilter - Initial bill status filter
 *
 * @returns {Object} Filter state, setters, and utilities
 */
export const useDispatchFilters = ({ initialState = {} } = {}) => {
  // =====================================================================
  // SEARCH STATE
  // =====================================================================

  /**
   * Current search text - searches across multiple fields:
   * name, origin, lot number, invoice, partner name, partner ref, finished products, container
   */
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm || '');

  /**
   * Flag to trigger search execution
   * Set to true when user clicks search button, effect will fetch and reset
   */
  const [search, setSearch] = useState(false);

  // =====================================================================
  // SORT/FILTER STATE
  // =====================================================================

  /**
   * Filter dropdown values
   * DISPATCH_COLUMN indices map to specific filter types:
   * - [15]: Bill Status filter (e.g., "All", "Created", "Not Created")
   * - [16]: Dispatch Status filter (e.g., "All Status", "Draft", "Dispatched")
   * - [17]: State filter (e.g., "All State", "Assigned", "Done")
   */
  const [dispatchStatusFilter, setDispatchStatusFilter] = useState(
    initialState.dispatchStatusFilter || DISPATCH_COLUMN[16].name,
  );
  const [stateFilter, setStateFilter] = useState(
    initialState.stateFilter || DISPATCH_COLUMN[17].name,
  );
  const [billStatusFilter, setBillStatusFilter] = useState(
    initialState.billStatusFilter || DISPATCH_COLUMN[15].name,
  );

  /**
   * Available options for each filter dropdown
   * Populated from API response via setters
   */
  const [dispatchStatusOptions, setDispatchStatusOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [billStatusOptions, setBillStatusOptions] = useState([]);

  // =====================================================================
  // UTILITY FUNCTIONS
  // =====================================================================

  /**
   * Builds the search portion of the API payload
   * When searchTerm is present, searches across multiple fields simultaneously
   * using OR logic (any field matching returns the record)
   *
   * @returns {Object} Search payload object with field:searchTerm pairs, or empty object
   */
  const buildSearchPayload = () => {
    return searchTerm
      ? {
          name: searchTerm,
          origin: searchTerm,
          'move_ids.future_lot': searchTerm,
          invoice_no: searchTerm,
          'partner_id.name': searchTerm,
          partner_ref: searchTerm,
          finished_products: searchTerm,
          physical_container_no: searchTerm,
        }
      : {};
  };

  /**
   * Builds the filter portion of the API payload
   * Converts dropdown selections into API filter parameters
   *
   * Only includes filters when they differ from the default (user made a selection)
   *
   * @returns {Object} Filter payload with selection filters and bill status
   */
  const buildFilterPayload = () => {
    return {
      selection: {
        ...(dispatchStatusFilter !== DISPATCH_COLUMN[16].name
          ? { dispatch_status: dispatchStatusFilter.toLowerCase() }
          : {}),
        ...(stateFilter !== DISPATCH_COLUMN[17].name
          ? { state: stateFilter.toLowerCase() }
          : {}),
      },
      ...(billStatusFilter !== DISPATCH_COLUMN[15].name
        ? { dispatch_bill_id: billStatusFilter.toLowerCase().replace(' ', '_') }
        : {}),
    };
  };

  /**
   * Checks if any filter has a non-default value
   *
   * @returns {boolean} True if any filter is not at default value
   */
  const hasNonDefaultFilters = () => {
    return (
      dispatchStatusFilter !== DISPATCH_COLUMN[16].name ||
      stateFilter !== DISPATCH_COLUMN[17].name ||
      billStatusFilter !== DISPATCH_COLUMN[15].name
    );
  };

  /**
   * Resets all filter dropdowns to their default values
   */
  const resetFilters = () => {
    setDispatchStatusFilter(DISPATCH_COLUMN[16].name);
    setStateFilter(DISPATCH_COLUMN[17].name);
    setBillStatusFilter(DISPATCH_COLUMN[15].name);
  };

  // =====================================================================
  // RETURN VALUE
  // =====================================================================

  return {
    // Search state
    searchTerm,
    setSearchTerm,
    search,
    setSearch,

    // Filter state
    dispatchStatusFilter,
    setDispatchStatusFilter,
    stateFilter,
    setStateFilter,
    billStatusFilter,
    setBillStatusFilter,

    // Dropdown options (populated externally by useDispatchData)
    dispatchStatusOptions,
    setDispatchStatusOptions,
    stateOptions,
    setStateOptions,
    billStatusOptions,
    setBillStatusOptions,

    // Utilities
    buildSearchPayload,
    buildFilterPayload,
    hasNonDefaultFilters,
    resetFilters,
  };
};
